import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaArrowRight, FaArrowLeft, FaCheck } from "react-icons/fa";
import api from "../../services/api";
import LikertBatteryQuestion from "./questions/LikertBatteryQuestion";
import SliderQuestion from "./questions/SliderQuestion";
import ButtonScaleQuestion from "./questions/ButtonScaleQuestion";
import SingleChoiceQuestion from "./questions/SingleChoiceQuestion";
import MultiChoiceQuestion from "./questions/MultiChoiceQuestion";
import OpenTextQuestion from "./questions/OpenTextQuestion";
import InfoPage from "./questions/InfoPage";
import DiscussionReminder from "./DiscussionReminder";
import NewsArticlePage from "./questions/NewsArticlePage";
import AttentionCheck from "./questions/AttentionCheck";
import StimulusPerceptionQuestion from "./questions/StimulusPerceptionQuestion";
import SearchableDropdown from "./questions/SearchableDropdown";

// Progress indicator (same pattern as OnboardingFlow).
// When `accent` is true, renders with the secondary accent palette — used on
// narrative pages so the chrome shifts colour along with the page itself.
const ProgressSteps = ({ currentStep, totalSteps, accent = false }) => {
  const filled = accent ? "bg-spiders-accent-500" : "bg-spiders-primary-500";
  const ring = accent ? "ring-spiders-accent-500/30" : "ring-spiders-primary-500/30";
  return (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: totalSteps }, (_, i) => (
        <React.Fragment key={i}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold transition-all duration-300 text-sm ${
              i < currentStep
                ? `${filled} text-white`
                : i === currentStep
                ? `${filled} text-white ring-4 ${ring}`
                : "bg-spiders-bg-elevated text-spiders-text-muted"
            }`}
          >
            {i < currentStep ? <FaCheck className="w-3 h-3" /> : i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div
              className={`w-8 h-0.5 mx-1 rounded transition-all duration-300 ${
                i < currentStep ? filled : "bg-spiders-bg-elevated"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const QuestionRenderer = ({ question, responses, onChange }) => {
  const { type, questionId, label } = question;

  return (
    <div className="mb-6">
      {type !== "info" && type !== "news_article" && type !== "stimulus_perception" && label && (
        <label className="block text-sm font-medium text-spiders-text-primary mb-3 whitespace-pre-line">
          {label}
          {question.required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}

      {type === "likert_battery" && (
        <LikertBatteryQuestion
          question={question}
          responses={responses}
          onChange={onChange}
        />
      )}

      {type === "slider" && (
        <SliderQuestion
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "button_scale" && (
        <ButtonScaleQuestion
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "single_choice" && (
        <SingleChoiceQuestion
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "multi_choice" && (
        <MultiChoiceQuestion
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "open_text" && (
        <OpenTextQuestion
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "info" && (
        <InfoPage
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "news_article" && (
        <NewsArticlePage
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "attention_check" && (
        <AttentionCheck
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}

      {type === "stimulus_perception" && (
        <StimulusPerceptionQuestion
          question={question}
          responses={responses}
          onChange={onChange}
        />
      )}

      {type === "searchable_dropdown" && (
        <SearchableDropdown
          question={question}
          value={responses[questionId]}
          onChange={onChange}
        />
      )}
    </div>
  );
};

const SurveyEngine = ({ surveyConfig, onComplete, title, storageKey, draftEndpoint, draftMeta }) => {
  // Restore saved responses from localStorage (survives page refresh)
  const [currentPageIndex, setCurrentPageIndex] = useState(() => {
    if (!storageKey) return 0;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved).pageIndex || 0;
    } catch (e) { /* ignore */ }
    return 0;
  });
  const [responses, setResponses] = useState(() => {
    if (!storageKey) return {};
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved).responses || {};
    } catch (e) { /* ignore */ }
    return {};
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const startedAtRef = useRef((() => {
    if (!storageKey) return Date.now();
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved).startedAt || Date.now();
    } catch (e) { /* ignore */ }
    return Date.now();
  })());
  const containerRef = useRef(null);

  const pages = surveyConfig?.pages || [];
  const currentPage = pages[currentPageIndex];

  // Scroll to top on initial mount — ensures first page (demographics) starts at top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // On mount: if sessionStorage is empty, try restoring from server-side draft
  useEffect(() => {
    if (!draftEndpoint || !draftMeta?.assignmentId || !draftMeta?.surveyType) return;
    // Only fetch if we have no local data (i.e., fresh session after browser close)
    if (Object.keys(responses).length > 0) return;

    const fetchDraft = async () => {
      try {
        const { data } = await api.get(draftEndpoint, {
          params: { assignmentId: draftMeta.assignmentId, surveyType: draftMeta.surveyType },
        });
        if (data.draft?.responses && Object.keys(data.draft.responses).length > 0) {
          setResponses(data.draft.responses);
          if (data.draft.startedAt) {
            startedAtRef.current = new Date(data.draft.startedAt).getTime();
          }
          // Find the furthest page that has all required answers filled
          let resumePage = 0;
          for (let i = 0; i < pages.length; i++) {
            const allAnswered = (pages[i].questions || [])
              .filter((q) => q.required && q.type !== "info")
              .every((q) => {
                if (q.type === "likert_battery") {
                  return (q.items || []).every((item) => data.draft.responses[`${q.questionId}_${item.itemId}`] != null);
                }
                return data.draft.responses[q.questionId] != null;
              });
            if (allAnswered) resumePage = i + 1;
            else break;
          }
          setCurrentPageIndex(Math.min(resumePage, pages.length - 1));
          console.log(`[SurveyEngine] Restored draft from server (page ${resumePage})`);
        }
      } catch (e) {
        // Non-critical — start from scratch
      }
    };
    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save responses + page index to sessionStorage on every change
  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        responses,
        pageIndex: currentPageIndex,
        startedAt: startedAtRef.current,
      }));
    } catch (e) { /* storage full — continue without saving */ }
  }, [responses, currentPageIndex, storageKey]);

  // Warn before closing/refreshing if there are unsaved responses
  useEffect(() => {
    const hasResponses = Object.keys(responses).length > 0;
    const handler = (e) => {
      if (hasResponses) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [responses]);

  // Save partial responses to server on every page advance (fire-and-forget)
  const saveDraft = useCallback((currentResponses) => {
    if (!draftEndpoint || !draftMeta?.assignmentId) return;
    api.patch(draftEndpoint, {
      assignmentId: draftMeta.assignmentId,
      surveyType: draftMeta.surveyType,
      responses: currentResponses,
      startedAt: new Date(startedAtRef.current).toISOString(),
    }).catch((e) => {
      console.warn("Draft save failed (non-critical):", e.message);
    });
  }, [draftEndpoint, draftMeta]);

  const handleChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
    // Clear validation error for this field
    setValidationErrors((prev) => prev.filter((e) => e !== questionId));
  };

  const validatePage = () => {
    const errors = [];
    if (!currentPage) return errors;

    for (const question of currentPage.questions || []) {
      if (!question.required) continue;
      if (question.type === "info" || question.type === "news_article") {
        if (question.required && responses[question.questionId] !== true) {
          errors.push(question.questionId);
        }
        continue;
      }

      if (question.type === "likert_battery") {
        for (const item of question.items || []) {
          const key = `${question.questionId}_${item.itemId}`;
          if (responses[key] === undefined || responses[key] === null) {
            errors.push(key);
          }
        }
      } else if (question.type === "stimulus_perception") {
        for (const item of question.perceptionItems || []) {
          const key = `${question.questionId}_${item.itemId}`;
          if (responses[key] === undefined || responses[key] === null) {
            errors.push(key);
          }
        }
      } else if (question.type === "multi_choice") {
        const val = responses[question.questionId];
        if (!Array.isArray(val) || val.length === 0) {
          errors.push(question.questionId);
        } else if (question.minSelections && val.length < question.minSelections) {
          errors.push(question.questionId);
        }
      } else if (question.type === "open_text") {
        const val = responses[question.questionId];
        if (!val || val.trim().length === 0) {
          errors.push(question.questionId);
        } else if (question.minLength && val.trim().length < question.minLength) {
          errors.push(question.questionId);
        }
      } else {
        if (responses[question.questionId] === undefined || responses[question.questionId] === null) {
          errors.push(question.questionId);
        }
      }
    }

    return errors;
  };

  const handleNext = () => {
    const errors = validatePage();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (currentPageIndex < pages.length - 1) {
      // Save partial responses to server on every page advance
      saveDraft(responses);
      setCurrentPageIndex((prev) => prev + 1);
      setValidationErrors([]);
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      // Last page — submit. Clear saved state first (responses are being submitted).
      if (storageKey) {
        try { sessionStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
      }
      const durationMs = Date.now() - startedAtRef.current;
      onComplete(responses, startedAtRef.current, durationMs);
    }
  };

  const handleBack = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex((prev) => prev - 1);
      setValidationErrors([]);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  if (!currentPage) {
    return null;
  }

  const isLastPage = currentPageIndex === pages.length - 1;

  return (
    <div ref={containerRef} className="max-w-2xl mx-auto px-4 py-6">
      {title && (
        <h1 className="text-xl font-bold text-spiders-text-primary text-center mb-2">
          {title}
        </h1>
      )}

      <ProgressSteps currentStep={currentPageIndex} totalSteps={pages.length} accent={!!currentPage.narrative} />

      {/* Page header — narrative pages get a distinct visual treatment
          (accent-coloured title, vertical accent bar, larger body text) so
          they read as scene/story rather than as another survey block. */}
      {currentPage.narrative ? (
        <div className="border-l-4 border-spiders-accent-500 pl-5 sm:pl-6 mb-8 bg-spiders-accent-500/10 py-6 rounded-r-lg">
          {currentPage.title && (
            <h2 className="text-2xl sm:text-3xl font-bold text-spiders-accent-300 mb-5 leading-tight tracking-tight">
              {currentPage.title}
            </h2>
          )}
          {currentPage.instruction && (
            <div className="space-y-4 text-spiders-text-secondary text-base leading-relaxed">
              {currentPage.instruction.split(/\n\n+/).map((para, paraIdx) => (
                <p key={paraIdx}>
                  {para
                    .split(/(\*\*[^*]+\*\*)/)
                    .map((part, i) =>
                      part.startsWith("**") && part.endsWith("**") ? (
                        <strong key={i} className="text-spiders-text-primary font-semibold">
                          {part.slice(2, -2)}
                        </strong>
                      ) : (
                        <React.Fragment key={i}>{part}</React.Fragment>
                      )
                    )}
                </p>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {currentPage.title && (
            <h2 className="text-lg font-semibold text-spiders-text-primary mb-2">
              {currentPage.title}
            </h2>
          )}
          {currentPage.instruction && (
            <p className="text-sm text-spiders-text-secondary mb-6 leading-relaxed whitespace-pre-line">
              {currentPage.instruction
                .split(/(\*\*[^*]+\*\*)/)
                .map((part, i) =>
                  part.startsWith("**") && part.endsWith("**") ? (
                    <strong key={i} className="text-spiders-text-primary font-semibold">
                      {part.slice(2, -2)}
                    </strong>
                  ) : (
                    <React.Fragment key={i}>{part}</React.Fragment>
                  )
                )}
            </p>
          )}
        </>
      )}

      {/* Discussion reminder — shows platform threads as memory refresher
          on perception pages where participants evaluate the discussion */}
      {(currentPage.showDiscussionReminder ||
        ["manipulation_checks", "perceived_polarization", "communicative_closure"].includes(currentPage.pageId)
      ) && <DiscussionReminder />}

      {/* Questions */}
      {(currentPage.questions || []).map((question) => (
        <QuestionRenderer
          key={question.questionId}
          question={question}
          responses={responses}
          onChange={handleChange}
        />
      ))}

      {/* Validation error message */}
      {validationErrors.length > 0 && (
        <p className="text-red-400 text-sm mb-4">
          Please answer all required questions before continuing.
        </p>
      )}

      {/* Navigation */}
      <div className="flex gap-3 mt-8">
        {currentPageIndex > 0 && (
          <button
            onClick={handleBack}
            className="flex-1 spiders-btn-secondary py-3 flex items-center justify-center gap-2"
          >
            <FaArrowLeft /> Back
          </button>
        )}
        <button
          onClick={handleNext}
          className={`flex-1 py-3 flex items-center justify-center gap-2 ${
            currentPage.narrative ? "spiders-btn-accent" : "spiders-btn-primary"
          }`}
        >
          {isLastPage ? "Submit" : "Continue"}{" "}
          {isLastPage ? <FaCheck /> : <FaArrowRight />}
        </button>
      </div>
    </div>
  );
};

export default SurveyEngine;
